// =============================================================================
// pollution-service.ts — pure-function port of Unity PollutionService.cs
// No class, no singleton, no module-level state.
// =============================================================================

import type { ActionLog } from "../rng.js";
import { assertFiniteInt } from "./_validation.js";

// ---------------------------------------------------------------------------
// PollutionState
// ---------------------------------------------------------------------------

export interface PollutionState {
  /** Global pollution counter (shared, floor 0). */
  readonly globalPollution: number;
}

// ---------------------------------------------------------------------------
// Result type
// ---------------------------------------------------------------------------

export interface AddPollutionResult {
  readonly newState: PollutionState;
  readonly log: ActionLog;
  /** Previous value before this change. */
  readonly prev: number;
  /** Next value after clamp. */
  readonly next: number;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns current global pollution.
 */
export function getPollution(state: PollutionState): number {
  return state.globalPollution;
}

/**
 * Apply `delta` to globalPollution. Clamps to 0 (matches Unity Mathf.Max(0, prev+delta)).
 * sourceSeat is null for global sources (mirrors Unity int? sourceSeat).
 */
export function addPollution(
  state: PollutionState,
  log: ActionLog,
  delta: number,
  sourceTag: string,
  sourceSeat: number | null,
): AddPollutionResult {
  // L: input validation
  assertFiniteInt(delta, "addPollution delta");
  if (sourceSeat !== null) assertFiniteInt(sourceSeat, "addPollution sourceSeat");
  const prev = state.globalPollution;
  const next = Math.max(0, prev + delta);
  const newState: PollutionState = Object.freeze({ globalPollution: next });
  const nextLog = log.append({
    kind: "pollution.add",
    data: {
      delta,
      sourceTag,
      sourceSeat: sourceSeat ?? null,
      prev,
      next,
    },
  });
  return { newState, log: nextLog, prev, next };
}

/**
 * Create initial pollution state.
 */
export function createPollutionState(initialValue = 0): PollutionState {
  // L: input validation
  assertFiniteInt(initialValue, "createPollutionState initialValue");
  return Object.freeze({ globalPollution: Math.max(0, initialValue) });
}
